/**
 * Longeva Pharma practitioner registration form.
 * Vanilla JS, no dependencies. WCAG 2.2 AA. Prefers-reduced-motion aware.
 *
 * Responsibilities:
 *  - Client-side field validation (required, email format, file type + size)
 *  - Inline per-field errors with aria-invalid + aria-describedby
 *  - Focus management: first invalid field on failed submit
 *  - fetch() POST as multipart/FormData to the configured endpoint
 *  - Loading state on submit button (disabled + "Submitting...")
 *  - Success panel swap on { ok: true } (or response.ok)
 *  - Error banner on network / server failure
 *  - Screen reader live region announcements
 *  - Optional Google Places address lookup (key-gated) that fills the
 *    professional address and postcode fields, matching checkout
 *  - Key-free postcode assist (postcodes.io): type-ahead postcode
 *    suggestions in a WAI-ARIA combobox listbox while the typed value is
 *    still partial, then canonical normalisation and a district
 *    confirmation in a quiet line once it is complete; it never blocks
 *    submission and degrades silently when unreachable
 */

(function () {
  'use strict';

  var ACCEPTED_TYPES = ['application/pdf', 'image/jpeg'];
  var ACCEPTED_EXTENSIONS = ['.pdf', '.jpg', '.jpeg'];
  var MAX_FILE_BYTES = 20 * 1024 * 1024; // 20 MB

  function init() {
    var form = document.getElementById('longeva-reg-form');
    if (!form) return;

    // Guard against double-binding if init runs twice (e.g. theme editor reload).
    if (form.dataset.longevaRegBound === 'true') return;
    form.dataset.longevaRegBound = 'true';

    // In-flight guard: blocks a second submit (repeat click or Enter) mid-request.
    var isSubmitting = false;

    var submitBtn  = form.querySelector('.longeva-reg__submit');
    var submitText = form.querySelector('.longeva-reg__submit-text');
    var spinner    = form.querySelector('.longeva-reg__spinner');
    var banner     = form.querySelector('.longeva-reg__banner--error');
    var successEl  = document.querySelector('.longeva-reg__success');
    var regBody   = document.querySelector('.longeva-reg__body');
    var liveRegion = document.querySelector('.longeva-reg__sr-live');
    // Wire every file upload area. The visible button proxies a click to its
    // clipped native input, and the file name reflects the current selection.
    // Scoped per area so each document field stays independent.
    var fileAreas = form.querySelectorAll('.longeva-reg__file-area');
    fileAreas.forEach(function (area) {
      var input  = area.querySelector('.longeva-reg__file-input');
      var btn    = area.querySelector('.longeva-reg__file-button');
      var nameEl = area.querySelector('.longeva-reg__file-name');
      if (!btn || !input) return;

      btn.addEventListener('click', function (e) {
        e.preventDefault();
        input.click();
      });

      input.addEventListener('change', function () {
        var file = input.files && input.files[0];
        if (nameEl) nameEl.textContent = file ? file.name : 'No file selected';
      });
    });

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      if (isSubmitting) return;
      if (!validateForm(form)) return;
      isSubmitting = true;
      submitForm(
        form, submitBtn, submitText, spinner, banner, successEl, regBody, liveRegion,
        function () { isSubmitting = false; }
      );
    });

    // Per-field blur validation for immediate feedback
    var fields = form.querySelectorAll('[data-validate]');
    fields.forEach(function (field) {
      field.addEventListener('blur', function () {
        validateField(field);
      });
    });

    initAddressLookup();
    initPostcodeAssist();
    initRegisteringBodyToggle();
  }

  // -------------------------------------------------------
  // Registering body conditional requirements
  // -------------------------------------------------------

  // A practitioner with no statutory register (body "None", typically an
  // aesthetic practitioner) has no registration number or expiry, so those two
  // fields become optional when None is selected and required again otherwise.
  // data-validate is read live by the validator, so toggling the attribute is
  // all that is needed; the visible asterisk follows.
  function initRegisteringBodyToggle() {
    var body = document.getElementById('reg-registering_body');
    var number = document.getElementById('reg-registration_number');
    var expiry = document.getElementById('reg-registration_expiry');
    if (!body || !number || !expiry) return;

    function setOptional(field, optional) {
      if (optional) {
        field.removeAttribute('required');
        field.removeAttribute('data-validate');
        field.removeAttribute('aria-required');
        clearError(field);
      } else {
        field.setAttribute('required', '');
        field.setAttribute('data-validate', 'required');
        field.setAttribute('aria-required', 'true');
      }
      var fieldWrap = field.closest('.longeva-reg__field');
      var star = fieldWrap ? fieldWrap.querySelector('.longeva-reg__label-required') : null;
      if (star) star.hidden = optional;
    }

    function sync() {
      var none = body.value === 'None';
      setOptional(number, none);
      setOptional(expiry, none);
    }

    body.addEventListener('change', sync);
    sync();
  }

  // -------------------------------------------------------
  // Validation
  // -------------------------------------------------------

  function validateForm(form) {
    var fields = form.querySelectorAll('[data-validate]');
    var firstInvalid = null;
    var isValid = true;

    fields.forEach(function (field) {
      var ok = validateField(field);
      if (!ok) {
        isValid = false;
        if (!firstInvalid) firstInvalid = field;
      }
    });

    if (!isValid && firstInvalid) {
      // The file input is visually clipped; move focus to its visible button
      // so keyboard and sighted users land on something they can see.
      var focusTarget = firstInvalid;
      if (firstInvalid.type === 'file') {
        var fileBtn = firstInvalid
          .closest('.longeva-reg__file-area')
          ? firstInvalid.closest('.longeva-reg__file-area').querySelector('.longeva-reg__file-button')
          : null;
        if (fileBtn) focusTarget = fileBtn;
      }
      focusTarget.focus();
    }
    return isValid;
  }

  function validateField(field) {
    clearError(field);

    var rules  = field.getAttribute('data-validate') || '';
    var value  = field.value;
    var error  = null;

    // Required
    if (rules.indexOf('required') !== -1) {
      if (field.type === 'checkbox' && !field.checked) {
        error = 'This box must be ticked to continue.';
      } else if (field.type === 'file') {
        if (!field.files || field.files.length === 0) {
          error = 'Please attach this document.';
        }
      } else if (value.trim() === '') {
        error = 'This field is required.';
      }
    }

    // Email format
    if (!error && rules.indexOf('email') !== -1 && value.trim() !== '') {
      if (!isValidEmail(value.trim())) {
        error = 'Please enter a valid email address.';
      }
    }

    // File type + size
    if (!error && field.type === 'file' && field.files && field.files.length > 0) {
      var file = field.files[0];
      if (!isAcceptedFile(file)) {
        error = 'Please upload a PDF or JPG file only.';
      } else if (file.size > MAX_FILE_BYTES) {
        error = 'The file is larger than 20 MB. Please compress or split it before uploading.';
      }
    }

    if (error) {
      showError(field, error);
      return false;
    }
    return true;
  }

  function isValidEmail(str) {
    // RFC-compliant enough for client-side UX; server validates definitively
    return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(str);
  }

  function isAcceptedFile(file) {
    var name = (file.name || '').toLowerCase();
    var matchesExt = ACCEPTED_EXTENSIONS.some(function (ext) {
      return name.endsWith(ext);
    });
    var matchesType = !!file.type && ACCEPTED_TYPES.indexOf(file.type) !== -1;
    // Accept on a known MIME type or a known extension. The server verifies the
    // real file type by magic bytes, so this is only a first-pass UX check.
    return matchesType || matchesExt;
  }

  function showError(field, message) {
    field.setAttribute('aria-invalid', 'true');
    var describedBy = field.getAttribute('aria-describedby') || '';
    var errorId = field.id + '-error';
    var errorEl = document.getElementById(errorId);
    if (errorEl) {
      errorEl.textContent = message;
      errorEl.classList.add('is-visible');
    }
    // Make sure aria-describedby includes the error element
    if (describedBy.indexOf(errorId) === -1) {
      field.setAttribute('aria-describedby', (describedBy + ' ' + errorId).trim());
    }

    // For file area wrapper
    var fileArea = field.closest('.longeva-reg__file-area');
    if (fileArea) {
      fileArea.setAttribute('aria-invalid', 'true');
    }
  }

  function clearError(field) {
    field.removeAttribute('aria-invalid');
    var errorId = field.id + '-error';
    var errorEl = document.getElementById(errorId);
    if (errorEl) {
      errorEl.textContent = '';
      errorEl.classList.remove('is-visible');
    }

    var fileArea = field.closest('.longeva-reg__file-area');
    if (fileArea) {
      fileArea.removeAttribute('aria-invalid');
    }
  }

  // -------------------------------------------------------
  // Submission
  // -------------------------------------------------------

  function submitForm(form, btn, btnText, spinner, banner, successEl, regBody, liveRegion, done) {
    if (typeof done !== 'function') { done = function () {}; }
    var endpoint = form.getAttribute('data-endpoint') || '/apps/practitioner-register';

    // Check honeypot
    var honeypot = form.querySelector('[name="website"]');
    if (honeypot && honeypot.value.trim() !== '') {
      // Bot detected; silently succeed to avoid enumeration
      showSuccess(successEl, regBody, liveRegion);
      done();
      return;
    }

    // If a Turnstile widget is present, require its token before sending so the
    // applicant gets an inline prompt instead of a rejected server round-trip.
    var turnstileWidget = form.querySelector('.cf-turnstile');
    if (turnstileWidget) {
      var turnstileToken = form.querySelector('[name="cf-turnstile-response"]');
      if (!turnstileToken || !turnstileToken.value) {
        showBanner(banner, 'Please complete the verification check below, then submit again.', liveRegion);
        done();
        return;
      }
    }

    setLoading(btn, btnText, spinner, true);
    hideBanner(banner);

    // Structured address (Alicia, 15 Jun 2026): combine the visible Address
    // line 1 + Town or city inputs into the hidden professional_address field
    // so the registry Worker and approval PDF keep the same field contract.
    var line1El = document.getElementById('reg-address_line1');
    var cityEl = document.getElementById('reg-city');
    var profEl = document.getElementById('reg-professional_address');
    if (profEl) {
      var l1 = line1El ? line1El.value.trim() : '';
      var ct = cityEl ? cityEl.value.trim() : '';
      profEl.value = ct ? (l1 + ', ' + ct) : l1;
    }

    var data = new FormData(form);

    // Abort a stalled upload rather than leave the applicant on a spinning
    // button forever. The window is generous because the submission carries
    // document uploads of up to 20 MB.
    var controller = window.AbortController ? new AbortController() : null;
    var timeout = controller ? setTimeout(function () { controller.abort(); }, 45000) : null;

    fetch(endpoint, {
      method: 'POST',
      body: data,
      signal: controller ? controller.signal : undefined
    })
      .then(function (res) {
        return res.json().then(function (body) {
          return { ok: res.ok, body: body };
        }).catch(function () {
          return { ok: res.ok, body: {} };
        });
      })
      .then(function (result) {
        if (timeout) clearTimeout(timeout);
        if (result.ok && result.body && result.body.ok !== false) {
          showSuccess(successEl, regBody, liveRegion);
        } else {
          var msg = (result.body && result.body.error) ||
          'Something went wrong. Please try again or contact us if the problem persists.';
          showBanner(banner, msg, liveRegion);
          setLoading(btn, btnText, spinner, false);
        }
        done();
      })
      .catch(function () {
        if (timeout) clearTimeout(timeout);
        showBanner(
          banner,
          'We could not reach the server. Please check your connection and try again.',
          liveRegion
        );
        setLoading(btn, btnText, spinner, false);
        done();
      });
  }

  function setLoading(btn, btnText, spinner, loading) {
    if (loading) {
      btn.disabled = true;
      btn.classList.add('is-loading');
      if (btnText) btnText.textContent = 'Submitting...';
    } else {
      btn.disabled = false;
      btn.classList.remove('is-loading');
      if (btnText) btnText.textContent = 'Submit application';
    }
  }

  function showSuccess(successEl, regBody, liveRegion) {
    if (regBody) regBody.style.display = 'none';
    if (successEl) {
      successEl.classList.add('is-visible');
      successEl.setAttribute('tabindex', '-1');
      successEl.focus();
    }
    announce(liveRegion, 'Your application has been submitted. Our Superintendent Pharmacist will review it and be in touch within one business day.');
  }

  function showBanner(banner, message, liveRegion) {
    if (!banner) return;
    banner.textContent = message;
    banner.classList.add('is-visible');
    announce(liveRegion, message);
    banner.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth', block: 'nearest' });
  }

  function hideBanner(banner) {
    if (!banner) return;
    banner.textContent = '';
    banner.classList.remove('is-visible');
  }

  function announce(liveRegion, message) {
    if (!liveRegion) return;
    liveRegion.textContent = '';
    // Tiny delay so the DOM mutation fires the live-region update
    setTimeout(function () {
      liveRegion.textContent = message;
    }, 50);
  }

  function prefersReducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  // -------------------------------------------------------
  // Address lookup (Google Places)
  // -------------------------------------------------------

  var PLACES_SCRIPT_ID = 'longeva-reg-places-js';

  /**
   * Checkout-style address search above the professional address field.
   * The wrapper renders only when the section has a Google Maps API key, so
   * on every other page (and with the key blank) nothing here runs and no
   * Google script is requested. The Maps JS loader is injected from here
   * rather than Liquid so the load and error handlers are attached before
   * the request starts: if the script is blocked or fails, the lookup hides
   * itself and manual entry carries on exactly as before.
   */
  function initAddressLookup() {
    var wrap = document.getElementById('reg-address-lookup-wrap');
    if (!wrap) return; // No API key configured; block not rendered.

    // Guard against double-binding if init runs twice (theme editor reload).
    if (wrap.dataset.longevaPlacesBound === 'true') return;
    wrap.dataset.longevaPlacesBound = 'true';

    var key = wrap.getAttribute('data-places-key');
    if (!key) {
      wrap.hidden = true;
      return;
    }

    // Library already on the page (for example a section reload after the
    // first successful load): bind straight away.
    if (window.google && window.google.maps && window.google.maps.importLibrary) {
      bindAddressLookup(wrap);
      return;
    }

    var script = document.getElementById(PLACES_SCRIPT_ID);
    if (script && script.dataset.longevaPlacesFailed === 'true') {
      wrap.hidden = true;
      return;
    }
    if (!script) {
      script = document.createElement('script');
      script.id = PLACES_SCRIPT_ID;
      script.async = true;
      script.src = 'https://maps.googleapis.com/maps/api/js' +
        '?key=' + encodeURIComponent(key) +
        '&v=weekly&libraries=places&loading=async&language=en-GB&region=GB';
      document.head.appendChild(script);
    }
    script.addEventListener('load', function () {
      bindAddressLookup(wrap);
    });
    script.addEventListener('error', function () {
      script.dataset.longevaPlacesFailed = 'true';
      wrap.hidden = true;
    });
  }

  function bindAddressLookup(wrap) {
    var host = wrap.querySelector('.longeva-reg__lookup-host');
    var addressField = document.getElementById('reg-professional_address');
    var postcodeField = document.getElementById('reg-postcode');
    if (!host || !addressField || !postcodeField) return;

    // One lookup element per host (re-entry safe across init paths).
    if (host.dataset.longevaPlacesReady === 'true') return;
    host.dataset.longevaPlacesReady = 'true';

    window.google.maps.importLibrary('places').then(function (placesLib) {
      // PlaceAutocompleteElement is the supported autocomplete surface for
      // new API keys; the classic Autocomplete class is closed to new
      // customers since March 2025.
      var lookup = new placesLib.PlaceAutocompleteElement({
        includedRegionCodes: ['gb'],
        includedPrimaryTypes: ['address']
      });
      lookup.id = 'reg-address-lookup';
      lookup.setAttribute('aria-label', 'Find your address');
      lookup.setAttribute('placeholder', 'Start typing your address');

      // Enter inside the lookup must never submit the application form.
      // The component's own selection handling is unaffected.
      lookup.addEventListener('keydown', function (event) {
        if (event.key === 'Enter') event.preventDefault();
      });

      lookup.addEventListener('gmp-select', function (event) {
        var prediction = event.placePrediction;
        if (!prediction) return;
        var place = prediction.toPlace();
        place
          .fetchFields({ fields: ['formattedAddress', 'addressComponents'] })
          .then(function () {
            fillAddressFromPlace(place, addressField, postcodeField);
          })
          .catch(function () {
            // Details fetch failed; the practitioner can type the address
            // into the fields below as normal.
          });
      });

      host.appendChild(lookup);
    }).catch(function () {
      // Library failed to initialise (bad key, quota, network). Hide the
      // lookup; the manual fields are unaffected.
      wrap.hidden = true;
    });
  }

  function fillAddressFromPlace(place, addressField, postcodeField) {
    var subpremise = '';
    var streetNumber = '';
    var route = '';
    var postalTown = '';
    var locality = '';
    var postcode = '';

    (place.addressComponents || []).forEach(function (component) {
      var types = component.types || [];
      if (types.indexOf('subpremise') !== -1) subpremise = component.longText || '';
      if (types.indexOf('street_number') !== -1) streetNumber = component.longText || '';
      if (types.indexOf('route') !== -1) route = component.longText || '';
      if (types.indexOf('postal_town') !== -1) postalTown = component.longText || '';
      if (types.indexOf('locality') !== -1) locality = component.longText || '';
      if (types.indexOf('postal_code') !== -1) postcode = component.longText || '';
    });

    var lines = [];
    var streetLine = [streetNumber, route].filter(Boolean).join(' ');
    if (subpremise) lines.push(subpremise);
    if (streetLine) lines.push(streetLine);
    // UK results usually carry postal_town rather than locality.
    var town = postalTown || locality;
    if (town) lines.push(town);
    if (lines.length === 0 && place.formattedAddress) {
      lines.push(place.formattedAddress);
    }

    addressField.value = lines.join('\n');
    if (postcode) postcodeField.value = postcode.toUpperCase();

    // Programmatic value changes fire no events on their own. Dispatch
    // input + change so anything listening observes the fill, then run the
    // existing validators to clear any lingering required errors.
    ['input', 'change'].forEach(function (type) {
      addressField.dispatchEvent(new Event(type, { bubbles: true }));
      postcodeField.dispatchEvent(new Event(type, { bubbles: true }));
    });
    validateField(addressField);
    validateField(postcodeField);

    // Land the practitioner on the filled address to review or edit it.
    addressField.focus();
  }

  // -------------------------------------------------------
  // Postcode assist + type-ahead suggestions (postcodes.io)
  // -------------------------------------------------------

  // Pragmatic UK postcode shape: outward code (area and district) then
  // inward code (sector and unit), with or without the space. Deliberately
  // loose; the API is the authority on whether the postcode exists, and the
  // server does not validate postcodes at all.
  var POSTCODE_PATTERN = /^[A-Z]{1,2}[0-9][0-9A-Z]?\s*[0-9][A-Z]{2}$/i;
  var POSTCODE_DEBOUNCE_MS = 400;
  var POSTCODE_TIMEOUT_MS = 3000;
  // Type-ahead starts at two characters; a single character matches half
  // the country and suggests nothing useful. Its debounce is a touch
  // shorter than the validation one so suggestions keep up with typing.
  var POSTCODE_SUGGEST_MIN_CHARS = 2;
  var POSTCODE_SUGGEST_DEBOUNCE_MS = 300;

  /**
   * Key-free postcode assist on the postcode field, powered by the open
   * postcodes.io dataset. Works with no API key, so it runs even when the
   * Google lookup above is dormant. Purely additive, in two halves.
   *
   * While the value is a partial postcode (two or more characters, not
   * yet postcode-shaped), a type-ahead listbox under the field offers
   * matching postcodes from the autocomplete endpoint. It follows the
   * WAI-ARIA combobox pattern, with the roles set from here so that
   * without the script the field stays a plain text input. Selecting a
   * suggestion (click, touch or Enter) fills the field, fires input and
   * change for anything listening, and hands straight to the second half.
   *
   * Once the value is postcode-shaped the listbox closes and the lookup
   * takes over: it normalises the value to the canonical spacing and case
   * and confirms the district in the quiet line under the field. An
   * unknown postcode gets a gentle nudge in the same line, never a
   * blocking error (a new-build postcode can be legitimately missing from
   * the dataset). On network failure or timeout both halves stand down
   * silently and the form behaves exactly as it does without the assist.
   * It is suggestion, validation and normalisation only: it never writes
   * to the address textarea.
   */
  function initPostcodeAssist() {
    var field = document.getElementById('reg-postcode');
    var assist = document.getElementById('reg-postcode-assist');
    if (!field || !assist) return;

    // Guard against double-binding if init runs twice (theme editor reload).
    if (field.dataset.longevaPostcodeBound === 'true') return;
    field.dataset.longevaPostcodeBound = 'true';

    var listbox = document.getElementById('reg-postcode-suggest');

    var debounceTimer = null;
    var activeController = null;

    // Listbox state: suggestions holds the strings currently rendered,
    // activeIndex the keyboard-highlighted option (-1 for none).
    var suggestions = [];
    var activeIndex = -1;
    var isOpen = false;
    // True while the assist line shows the suggestion count, so closing
    // the listbox can clear that message (it describes the listbox) while
    // leaving district confirmations and nudges (they describe the value).
    var assistShowsCount = false;

    // Combobox semantics go on from here, not Liquid, so a missing or
    // failed script leaves an ordinary text input with no references to
    // a listbox nothing will ever fill.
    if (listbox) {
      field.setAttribute('role', 'combobox');
      field.setAttribute('aria-expanded', 'false');
      field.setAttribute('aria-controls', 'reg-postcode-suggest');
      field.setAttribute('aria-autocomplete', 'list');
    }

    field.addEventListener('input', function () {
      if (debounceTimer) clearTimeout(debounceTimer);
      var value = field.value.trim();
      if (POSTCODE_PATTERN.test(value)) {
        // A complete postcode belongs to the validate-and-normalise path;
        // a suggestion for it could only repeat the value back.
        closeSuggest();
        debounceTimer = setTimeout(lookupPostcode, POSTCODE_DEBOUNCE_MS);
      } else if (listbox && compact(value).length >= POSTCODE_SUGGEST_MIN_CHARS) {
        debounceTimer = setTimeout(lookupSuggestions, POSTCODE_SUGGEST_DEBOUNCE_MS);
      } else {
        // Too short to act on: stand both halves down.
        abortActive();
        closeSuggest();
        setAssist('', false);
      }
    });

    field.addEventListener('blur', function () {
      if (debounceTimer) clearTimeout(debounceTimer);
      // Selecting with the pointer never blurs the field (the listbox
      // swallows mousedown), so a blur here is a real departure.
      closeSuggest();
      lookupPostcode();
    });

    field.addEventListener('keydown', function (event) {
      if (!isOpen) return;
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        moveActive(1);
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        moveActive(-1);
      } else if (event.key === 'Enter') {
        // While the listbox is open, Enter picks; it never submits.
        event.preventDefault();
        if (activeIndex >= 0 && suggestions[activeIndex]) {
          selectSuggestion(suggestions[activeIndex]);
        } else {
          closeSuggest();
        }
      } else if (event.key === 'Escape') {
        event.preventDefault();
        closeSuggest();
      } else if (event.key === 'Tab') {
        // No preventDefault: focus moves on as normal, minus the listbox.
        closeSuggest();
      }
    });

    if (listbox) {
      // Pressing on an option must not blur the field, or the blur
      // handler above would close the listbox before the click lands.
      listbox.addEventListener('mousedown', function (event) {
        event.preventDefault();
      });

      // One delegated handler covers click and touch for every option.
      listbox.addEventListener('click', function (event) {
        var option = event.target.closest('[role="option"]');
        if (!option) return;
        var index = parseInt(option.getAttribute('data-index') || '', 10);
        if (isNaN(index) || !suggestions[index]) return;
        selectSuggestion(suggestions[index]);
      });

      // A click anywhere outside the combobox closes the listbox and
      // leaves the field value exactly as typed. The handler lives on the
      // document, which outlives the section in the theme editor, so it
      // unbinds itself once this field has been torn out of the DOM
      // (section reload re-inits on fresh nodes); re-init therefore never
      // stacks document listeners or pins discarded nodes in memory.
      document.addEventListener('click', function onDocumentClick(event) {
        if (!document.documentElement.contains(field)) {
          document.removeEventListener('click', onDocumentClick);
          return;
        }
        if (!isOpen) return;
        if (event.target === field || listbox.contains(event.target)) return;
        closeSuggest();
      });
    }

    function setAssist(message, isWarning) {
      assistShowsCount = false;
      var warn = !!isWarning;
      // Skip identical rewrites: the line is an aria-live region, and
      // re-setting unchanged text makes some screen readers announce the
      // same message again on every debounced response.
      if (assist.textContent === message &&
          assist.classList.contains('longeva-reg__postcode-assist--warn') === warn) {
        return;
      }
      assist.textContent = message;
      assist.classList.toggle('longeva-reg__postcode-assist--warn', warn);
    }

    // The suggestion count rides the same quiet line but is tracked, so
    // closing the listbox can take the count away with it.
    function setAssistCount(count) {
      setAssist(
        count === 1
          ? '1 postcode suggestion available'
          : count + ' postcode suggestions available',
        false
      );
      assistShowsCount = true;
    }

    // Spacing-and-case-insensitive comparison, so a response is only applied
    // while the field still holds the value that was looked up.
    function compact(value) {
      return value.replace(/\s+/g, '').toUpperCase();
    }

    // Only the latest value matters; drop any in-flight request, whichever
    // half of the assist started it.
    function abortActive() {
      if (activeController) {
        activeController.abort();
        activeController = null;
      }
    }

    // ---- Type-ahead half ----

    function lookupSuggestions() {
      var value = field.value.trim();

      abortActive();

      // The value may have changed shape during the debounce: a complete
      // postcode belongs to lookupPostcode, anything under the minimum to
      // nobody.
      if (POSTCODE_PATTERN.test(value) ||
          compact(value).length < POSTCODE_SUGGEST_MIN_CHARS) {
        closeSuggest();
        return;
      }

      var controller = window.AbortController ? new AbortController() : null;
      activeController = controller;
      var timeoutId = controller
        ? setTimeout(function () { controller.abort(); }, POSTCODE_TIMEOUT_MS)
        : null;

      fetch('https://api.postcodes.io/postcodes/' + encodeURIComponent(value) + '/autocomplete', {
        signal: controller ? controller.signal : undefined,
        credentials: 'omit'
      })
        .then(function (res) {
          if (!res.ok) return null;
          return res.json();
        })
        .then(function (body) {
          if (timeoutId) clearTimeout(timeoutId);
          if (activeController === controller) activeController = null;

          // The practitioner kept typing; this response is stale.
          if (field.value.trim() !== value) return;

          var results = body && Array.isArray(body.result) ? body.result : null;
          if (!results || results.length === 0) {
            // Nothing to offer: close quietly and let typing carry on.
            closeSuggest();
            setAssist('', false);
            return;
          }

          // Focus moved elsewhere while the request was in flight; opening
          // a listbox under an unfocused field would strand it.
          if (document.activeElement !== field) return;

          renderSuggestions(results);
          // The count goes through the quiet line (aria-live polite) only
          // when the listbox opens, so screen readers hear what appeared.
          setAssistCount(results.length);
        })
        .catch(function () {
          // Abort, timeout or network failure: clear this request's timer,
          // then close quietly; the field carries on as a plain input. A
          // superseded request must not touch the listbox: the newer
          // lookup owns it.
          if (timeoutId) clearTimeout(timeoutId);
          if (activeController !== controller) return;
          activeController = null;
          closeSuggest();
          setAssist('', false);
        });
    }

    function renderSuggestions(results) {
      // Rebuilt from scratch on every response. Suggestion text lands via
      // textContent only, so nothing from the network is parsed as HTML.
      while (listbox.firstChild) listbox.removeChild(listbox.firstChild);
      suggestions = results;
      activeIndex = -1;
      results.forEach(function (text, index) {
        var option = document.createElement('div');
        option.className = 'longeva-reg__suggest-option';
        option.id = 'reg-postcode-option-' + index;
        option.setAttribute('role', 'option');
        option.setAttribute('aria-selected', 'false');
        option.setAttribute('data-index', String(index));
        option.textContent = text;
        listbox.appendChild(option);
      });
      listbox.hidden = false;
      isOpen = true;
      field.setAttribute('aria-expanded', 'true');
      field.removeAttribute('aria-activedescendant');
    }

    function closeSuggest() {
      if (!listbox) return;
      suggestions = [];
      activeIndex = -1;
      isOpen = false;
      while (listbox.firstChild) listbox.removeChild(listbox.firstChild);
      listbox.hidden = true;
      field.setAttribute('aria-expanded', 'false');
      field.removeAttribute('aria-activedescendant');
      // The suggestion count describes the listbox, so it must not outlive
      // it; district confirmations and nudges describe the value and stay.
      if (assistShowsCount) setAssist('', false);
    }

    function moveActive(step) {
      if (!suggestions.length) return;
      var next = activeIndex + step;
      if (next < 0) next = suggestions.length - 1;
      if (next >= suggestions.length) next = 0;
      setActive(next);
    }

    function setActive(index) {
      var options = listbox.children;
      if (activeIndex >= 0 && options[activeIndex]) {
        options[activeIndex].classList.remove('is-active');
        options[activeIndex].setAttribute('aria-selected', 'false');
      }
      activeIndex = index;
      var option = options[index];
      if (!option) return;
      option.classList.add('is-active');
      option.setAttribute('aria-selected', 'true');
      field.setAttribute('aria-activedescendant', option.id);
      // Keep the highlighted option in view inside the scrolling listbox.
      if (option.scrollIntoView) option.scrollIntoView({ block: 'nearest' });
    }

    function selectSuggestion(text) {
      closeSuggest();
      field.value = text;
      // Programmatic value changes fire no events on their own. Dispatch
      // input + change so anything listening observes the fill. The input
      // handler above schedules the debounced lookup; cancel that and run
      // it now, so the district confirmation appears straight away.
      ['input', 'change'].forEach(function (type) {
        field.dispatchEvent(new Event(type, { bubbles: true }));
      });
      if (debounceTimer) clearTimeout(debounceTimer);
      lookupPostcode();
    }

    // ---- Validate-and-normalise half ----

    function lookupPostcode() {
      var value = field.value.trim();

      abortActive();

      if (!POSTCODE_PATTERN.test(value)) {
        setAssist('', false);
        return;
      }

      var controller = window.AbortController ? new AbortController() : null;
      activeController = controller;
      var timeoutId = controller
        ? setTimeout(function () { controller.abort(); }, POSTCODE_TIMEOUT_MS)
        : null;

      fetch('https://api.postcodes.io/postcodes/' + encodeURIComponent(value), {
        signal: controller ? controller.signal : undefined,
        credentials: 'omit'
      })
        .then(function (res) {
          if (res.status === 404) return { notFound: true };
          if (!res.ok) return null;
          return res.json();
        })
        .then(function (body) {
          if (timeoutId) clearTimeout(timeoutId);
          if (activeController === controller) activeController = null;

          // The practitioner kept typing; this response is stale.
          if (compact(field.value) !== compact(value)) return;

          // The field provably holds a postcode-shaped value, so a stale
          // "required" error from an earlier empty blur is wrong whichever
          // way the lookup resolved. Re-run the validator to clear it
          // before the assist line speaks, so red and amber never disagree.
          validateField(field);

          if (body && body.notFound) {
            setAssist('We could not find this postcode, please check it', true);
            return;
          }

          var result = body && body.result;
          if (!result || !result.postcode) {
            setAssist('', false);
            return;
          }

          // Normalise to the canonical formatting (spacing and case). Skip
          // the rewrite while the caret sits mid-edit inside the field, as
          // setting the value would jump it to the end; the blur lookup
          // applies the canonical form once editing finishes.
          var caretAtEnd = field.selectionStart === field.value.length &&
            field.selectionEnd === field.value.length;
          if ((document.activeElement !== field || caretAtEnd) &&
              field.value.trim() !== result.postcode) {
            field.value = result.postcode;
          }

          var place = result.admin_district || result.parish || result.region || '';
          // ONS inverts some district names ("Bristol, City of"); flip them
          // back to natural order for the confirmation line.
          var inverted = place.match(/^(.+), (City|County|Royal Borough|Borough) of$/);
          if (inverted) place = inverted[2] + ' of ' + inverted[1];
          setAssist(place ? 'Postcode found: ' + place : 'Postcode found', false);

          // Postcode assist (free postcodes.io): populate the Town or city
          // field from the postcode when it is still empty, so the address
          // entry is quicker. The bare district name (not the inverted "City
          // of X" form) reads best in a city field. Never overwrite a value
          // the practitioner has already typed; they can always edit it.
          var cityName = inverted ? inverted[1] : place;
          var cityField = document.getElementById('reg-city');
          if (cityField && !cityField.value.trim() && cityName) {
            cityField.value = cityName;
            cityField.dispatchEvent(new Event('input', { bubbles: true }));
            cityField.dispatchEvent(new Event('change', { bubbles: true }));
            validateField(cityField);
          }
        })
        .catch(function () {
          // Abort, timeout or network failure: always clear this request's
          // timer, then clear the line and stay quiet. A superseded request
          // must not touch the line: the newer lookup owns it.
          if (timeoutId) clearTimeout(timeoutId);
          if (activeController !== controller) return;
          activeController = null;
          setAssist('', false);
        });
    }
  }

  // -------------------------------------------------------
  // Kick off after DOM is ready
  // -------------------------------------------------------

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-init when the section is reloaded in the Shopify theme editor.
  // init() is idempotent (guards against double-binding), so this is safe.
  document.addEventListener('shopify:section:load', function (event) {
    if (event.target && event.target.querySelector &&
        event.target.querySelector('#longeva-reg-form')) {
      init();
    }
  });
}());
